import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getProduttori, getScadenze, createProduttore, updateProduttore } from "../lib/api";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Search, ChevronRight, MapPin, Clock, Plus, AlertTriangle } from "lucide-react";
import {
  FREQUENZE, FREQUENZE_LABEL, ALTERNANZE, SETTIMANE_MESE, MESI_ALTERNANZA, FASCE_VISITA,
  GIORNI, daysUntil, formatDateIt, classifyScadenza, SCADENZA_LABEL, SCADENZA_COLOR,
} from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { useZoneAttive } from "../hooks/useZone";
import { GeocodingFallbackDialog } from "../components/GeocodingFallbackDialog";
import { toast } from "sonner";

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function Chip({ active, children, onClick, testid, color }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex-shrink-0 snap-start rounded-full px-4 h-10 flex items-center justify-center text-sm font-medium whitespace-nowrap transition-colors ${
        active
          ? "text-white border"
          : "bg-white text-slate-700 border border-slate-200 hover:border-[#1B5E20]/40"
      }`}
      style={active ? { backgroundColor: color || "#1B5E20", borderColor: color || "#1B5E20" } : {}}
    >
      {children}
    </button>
  );
}

const STATI_CONTRATTO = [
  { v: "ATTIVO", label: "Attivo", color: "#1B5E20" },
  { v: "IN_SCADENZA", label: "In scadenza", color: "#F57C00" },
  { v: "URGENTE", label: "Urgente", color: "#C62828" },
  { v: "SCADUTO", label: "Scaduto", color: "#7F1D1D" },
];

export default function ProduttoriList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [produttori, setProduttori] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scadenzeCfg, setScadenzeCfg] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const { user } = useAuth();
  const { zone } = useZoneAttive();

  const [q, setQ] = useState(searchParams.get("q") || "");
  const zona = searchParams.get("zona") || "";
  const frequenza = searchParams.get("frequenza") || "";
  const stato = searchParams.get("stato") || "";
  // P1 — filtro "Solo orario da verificare" (badge arancio)
  const orarioDaVerificare = searchParams.get("orarioDaVerificare") === "1";
  // Push 5: filtro attivi/inattivi/tutti e paginazione
  const attivoFilter = searchParams.get("attivi") || "attivi"; // attivi | inattivi | tutti
  const pageSize = parseInt(searchParams.get("ps") || "50", 10);
  const page = parseInt(searchParams.get("p") || "1", 10);

  const debouncedQ = useDebounced(q, 300);

  const canCreate = user && (user.ruolo === "AMMINISTRATORE" || user.ruolo === "UFFICIO");

  useEffect(() => {
    getScadenze().then((r) => setScadenzeCfg(r.config));
  }, []);

  const reload = () => {
    setLoading(true);
    const params = {
      q: debouncedQ || undefined,
      zona: zona || undefined,
      frequenza: frequenza || undefined,
      stato_contratto: stato || undefined,
      paginated: true,
      limit: pageSize,
      skip: (page - 1) * pageSize,
    };
    if (attivoFilter === "attivi") params.attivo = true;
    else if (attivoFilter === "inattivi") params.attivo = false;
    if (orarioDaVerificare) params.orarioDaVerificare = true;
    getProduttori(params)
      .then((data) => {
        if (data && typeof data === "object" && Array.isArray(data.items)) {
          setProduttori(data.items);
          setTotal(data.total || 0);
        } else if (Array.isArray(data)) {
          setProduttori(data);
          setTotal(data.length);
        } else {
          setProduttori([]);
          setTotal(0);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, zona, frequenza, stato, attivoFilter, orarioDaVerificare, pageSize, page]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (debouncedQ) next.set("q", debouncedQ); else next.delete("q");
    // Reset paginazione su nuovo testo cerca
    next.delete("p");
    localStorage.setItem("produttori_filters", next.toString());
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    // Reset pagina su cambio filtro
    next.delete("p");
    setSearchParams(next, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const filtered = produttori; // filtro server-side

  return (
    <div className="space-y-4" data-testid="produttori-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Produttori</h1>
          <p className="text-slate-500 text-sm mt-1">Anagrafica attività</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-600 font-semibold" data-testid="produttori-counter">
            {filtered.length} di {total}
          </div>
          <Link to="/produttori/da-rivedere">
            <Button
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-50 min-h-[44px]"
              data-testid="btn-da-rivedere"
            >
              <AlertTriangle size={16} className="mr-1" /> Da rivedere
            </Button>
          </Link>
          {canCreate && (
            <Button
              onClick={() => setOpenCreate(true)}
              className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[44px]"
              data-testid="btn-new-produttore"
            >
              <Plus size={16} className="mr-1" /> Nuovo produttore
            </Button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per ragione sociale…"
          className="pl-10 h-11 bg-white"
        />
      </div>

      <div className="flex overflow-x-auto snap-x hide-scrollbar gap-2 py-1" data-testid="filter-zone-row">
        <Chip active={!zona} onClick={() => setFilter("zona", "")} testid="zona-chip-all">Tutte zone</Chip>
        {zone.map((z) => (
          <Chip
            key={z.id}
            active={zona === z.nome}
            onClick={() => setFilter("zona", z.nome)}
            testid={`zona-chip-${z.nome}`}
            color={z.colore}
          >
            {z.nome}
          </Chip>
        ))}
      </div>

      <div className="flex overflow-x-auto snap-x hide-scrollbar gap-2 py-1" data-testid="filter-freq-row">
        <Chip active={!frequenza} onClick={() => setFilter("frequenza", "")} testid="freq-chip-all">Tutte freq.</Chip>
        {FREQUENZE.map((f) => (
          <Chip key={f} active={frequenza === f} onClick={() => setFilter("frequenza", f)} testid={`freq-chip-${f}`}>
            {FREQUENZE_LABEL[f]}
          </Chip>
        ))}
      </div>

      <div className="flex overflow-x-auto snap-x hide-scrollbar gap-2 py-1" data-testid="filter-stato-row">
        <Chip active={!stato} onClick={() => setFilter("stato", "")} testid="stato-chip-all">Tutti contratti</Chip>
        {STATI_CONTRATTO.map((s) => (
          <Chip
            key={s.v}
            active={stato === s.v}
            onClick={() => setFilter("stato", s.v)}
            testid={`stato-chip-${s.v}`}
            color={s.color}
          >
            {s.label}
          </Chip>
        ))}
      </div>

      <div className="flex overflow-x-auto snap-x hide-scrollbar gap-2 py-1" data-testid="filter-attivo-row">
        <Chip active={attivoFilter === "attivi"} onClick={() => setFilter("attivi", "")} testid="attivo-chip-attivi" color="#1B5E20">Solo attivi</Chip>
        <Chip active={attivoFilter === "inattivi"} onClick={() => setFilter("attivi", "inattivi")} testid="attivo-chip-inattivi" color="#64748B">Disattivati</Chip>
        <Chip active={attivoFilter === "tutti"} onClick={() => setFilter("attivi", "tutti")} testid="attivo-chip-tutti">Tutti</Chip>
      </div>

      {/* P1 — Filtro "Solo orario da verificare" (badge arancio) */}
      <div className="flex items-center gap-2 py-1" data-testid="filter-orario-row">
        <Chip
          active={orarioDaVerificare}
          onClick={() => setFilter("orarioDaVerificare", orarioDaVerificare ? "" : "1")}
          testid="orario-da-verificare-chip"
          color="#F57C00"
        >
          <AlertTriangle size={14} className="mr-1.5 -ml-0.5" />
          Solo orario da verificare
        </Chip>
        {orarioDaVerificare && (
          <span className="text-xs text-[#E65100] font-medium" data-testid="orario-da-verificare-hint">
            Risultati con fascia visita DA VERIFICARE
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-slate-500 p-4">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="produttori-empty">
          Nessun produttore trovato
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="produttori-list">
          {filtered.map((p) => {
            const c = p.contratto || {};
            const cls = classifyScadenza(c.dataScadenza, scadenzeCfg);
            const days = daysUntil(c.dataScadenza);
            return (
              <li key={p.id}>
                <Link to={`/produttori/${p.id}`} data-testid={`producer-card-${p.id}`} className="block">
                  <Card className="p-4 bg-white border border-slate-200 shadow-sm card-hover">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{p.ragioneSociale}</div>
                          {!p.attivo && <Badge className="bg-slate-400 text-white text-[10px]">DISATTIVATO</Badge>}
                          {c.gratuito ? (
                            <Badge className="bg-[#1B5E20]/10 text-[#1B5E20] hover:bg-[#1B5E20]/15">GRATUITO</Badge>
                          ) : c.prezzoEuroKg != null ? (
                            <Badge className="bg-[#1B5E20] text-white hover:bg-[#144818]">{c.prezzoEuroKg.toFixed(2)} €/kg</Badge>
                          ) : null}
                          {c.spurgo ? <Badge className="bg-[#F57C00]/15 text-[#F57C00] hover:bg-[#F57C00]/20">SPURGO {c.spurgo}</Badge> : null}
                          {cls && cls !== "ok" && (
                            <Badge data-testid={`scadenza-badge-${cls}`} className="text-[10px]"
                              style={{ backgroundColor: SCADENZA_COLOR[cls], color: "#fff" }}>
                              {SCADENZA_LABEL[cls]}
                            </Badge>
                          )}
                          {(p.fasciaOrariaVisitaEffettiva === "DA_VERIFICARE" || p.fasciaOrariaVisitaSospetta) && (
                            <Badge
                              data-testid={`orario-da-verificare-badge-${p.id}`}
                              className="text-[10px] bg-[#F57C00] text-white hover:bg-[#E65100]"
                              title="Fascia oraria di visita mancante o sospetta — verifica la scheda"
                            >
                              <AlertTriangle size={10} className="mr-1 -ml-0.5" />
                              Orario da verificare
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <MapPin size={13} /> {p.zona || "-"}
                          {p.cap || p.citta ? ` · ${[p.citta, p.cap].filter(Boolean).join(", ")}` : ""}
                          {" · "}{FREQUENZE_LABEL[c.frequenza] || c.frequenza || "-"}
                        </div>
                        <div className="text-xs mt-1 flex items-center gap-1">
                          <Clock size={13} style={{ color: cls && cls !== "ok" ? SCADENZA_COLOR[cls] : "#94a3b8" }} />
                          <span style={{ color: cls && cls !== "ok" ? SCADENZA_COLOR[cls] : "#64748b" }} className={cls && cls !== "ok" ? "font-semibold" : ""}>
                            Scadenza: {formatDateIt(c.dataScadenza)}
                            {cls === "scaduto" ? " (scaduto)" : (days != null && days >= 0) ? ` (${days} gg)` : ""}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-slate-400" />
                    </div>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {openCreate && (
        <NuovoProduttoreDialog
          zone={zone}
          onClose={() => setOpenCreate(false)}
          onCreated={() => { setOpenCreate(false); reload(); }}
        />
      )}

      {!loading && total > 0 && (
        <div className="flex items-center justify-between gap-3 py-3 border-t border-slate-200" data-testid="pagination-bar">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>Per pagina:</span>
            {[50, 100, 200].map((n) => (
              <button
                key={n}
                onClick={() => setFilter("ps", n === 50 ? "" : String(n))}
                data-testid={`page-size-${n}`}
                className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                  pageSize === n ? "bg-[#1B5E20] text-white" : "bg-white border border-slate-200 text-slate-700"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter("p", String(Math.max(1, page - 1)))}
              disabled={page <= 1}
              data-testid="page-prev"
              className="h-9 px-3 rounded-md text-sm bg-white border border-slate-200 text-slate-700 disabled:opacity-40 min-h-[36px]"
            >
              ← Prec
            </button>
            <span className="text-xs text-slate-600" data-testid="page-indicator">
              Pagina <b>{page}</b> di <b>{totalPages}</b>
            </span>
            <button
              onClick={() => setFilter("p", String(Math.min(totalPages, page + 1)))}
              disabled={page >= totalPages}
              data-testid="page-next"
              className="h-9 px-3 rounded-md text-sm bg-white border border-slate-200 text-slate-700 disabled:opacity-40 min-h-[36px]"
            >
              Succ →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function NuovoProduttoreDialog({ zone, onClose, onCreated }) {
  const [form, setForm] = useState({
    ragioneSociale: "", indirizzo: "", citta: "", cap: "", zonaId: "",
    telefonoFisso: "", telefonoMobile: "", partitaIva: "", orarioRitiro: "",
    fasciaOrariaVisita: "SEMPRE_APERTO",
    note: "",
    contratto: {
      frequenza: "SETTIMANALE", gratuito: false, prezzoEuroKg: null,
      dataScadenza: null, rinnovoTacito: "NESSUNO",
      alternanza: null, settimanaMese: null, meseAlternanza: null,
    },
    pianificazione: { giorno: "LUN" },
  });
  const [submitting, setSubmitting] = useState(false);
  const [fallbackProd, setFallbackProd] = useState(null); // {id, citta} se geocoding_failed

  const setF = (k, v) => setForm({ ...form, [k]: v });
  const setC = (k, v) => setForm({ ...form, contratto: { ...form.contratto, [k]: v } });
  const setP = (k, v) => setForm({ ...form, pianificazione: { ...form.pianificazione, [k]: v } });

  // Blocco A3 + B6.0: cambio frequenza con auto-clear / proposta di default
  const onChangeFreq = (newFreq) => {
    const next = { ...form.contratto, frequenza: newFreq };
    if (newFreq === "SETTIMANALE" || newFreq === "SU_APPUNTAMENTO") {
      next.alternanza = null;
      next.settimanaMese = null;
      next.meseAlternanza = null;
    } else if (newFreq === "BISETTIMANALE") {
      next.alternanza = next.alternanza || "DISPARI";
      next.settimanaMese = null;
      next.meseAlternanza = null;
    } else if (newFreq === "MENSILE") {
      next.settimanaMese = next.settimanaMese || "W1";
      next.alternanza = null;
      next.meseAlternanza = null;
    } else if (newFreq === "BIMESTRALE") {
      next.settimanaMese = next.settimanaMese || "W1";
      next.meseAlternanza = next.meseAlternanza || "PARI";
      next.alternanza = null;
    }
    setForm({ ...form, contratto: next });
  };

  const freq = form.contratto.frequenza;
  const showGiorno = freq !== "SU_APPUNTAMENTO";
  const showAlternanza = freq === "BISETTIMANALE";
  const showSettimanaMese = freq === "MENSILE" || freq === "BIMESTRALE";
  const showMeseAlternanza = freq === "BIMESTRALE";

  const onSubmit = async () => {
    if (!form.ragioneSociale.trim()) { toast.error("Denominazione obbligatoria"); return; }
    if (!form.indirizzo.trim()) { toast.error("Indirizzo obbligatorio"); return; }
    if (!form.citta.trim()) { toast.error("Città obbligatoria"); return; }
    if (!/^\d{5}$/.test(form.cap)) { toast.error("CAP deve essere 5 cifre"); return; }
    if (!form.zonaId) { toast.error("Zona obbligatoria"); return; }
    const zona = zone.find((z) => z.id === form.zonaId);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        zona: zona?.nome || "",
        contratto: {
          ...form.contratto,
          alternanza: showAlternanza ? (form.contratto.alternanza || "DISPARI") : null,
          settimanaMese: showSettimanaMese ? (form.contratto.settimanaMese || "W1") : null,
          meseAlternanza: showMeseAlternanza ? (form.contratto.meseAlternanza || "PARI") : null,
        },
        pianificazione: showGiorno
          ? { giorno: form.pianificazione.giorno }
          : { giorno: null },
      };
      const created = await createProduttore(payload);
      if (created.geocoding_failed) {
        // Apri dialog mappa per scegliere coord manualmente (BUG #1 fallback)
        setFallbackProd({ id: created.id, citta: form.citta });
        toast.warning("Indirizzo non trovato. Posiziona il pin sulla mappa.");
      } else {
        toast.success("Produttore creato");
        onCreated();
      }
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore creazione produttore");
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmFallback = async (lat, lng) => {
    if (!fallbackProd) return;
    try {
      await updateProduttore(fallbackProd.id, { latitudine: lat, longitudine: lng });
      toast.success("Coordinate salvate");
      setFallbackProd(null);
      onCreated();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore salvataggio coordinate");
    }
  };

  return (
    <>
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-new-produttore">
        <DialogHeader>
          <DialogTitle>Nuovo produttore</DialogTitle>
          <DialogDescription>
            Inserisci anagrafica e contratto. L'indirizzo verrà geocodificato automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Denominazione *</Label>
            <Input data-testid="np-denominazione" value={form.ragioneSociale}
              onChange={(e) => setF("ragioneSociale", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2">
              <Label>Indirizzo *</Label>
              <Input data-testid="np-indirizzo" placeholder="Via Roma 12"
                value={form.indirizzo} onChange={(e) => setF("indirizzo", e.target.value)} />
            </div>
            <div>
              <Label>CAP *</Label>
              <Input data-testid="np-cap" placeholder="50100" maxLength={5}
                value={form.cap} onChange={(e) => setF("cap", e.target.value.replace(/\D/g, ""))} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>Città *</Label>
              <Input data-testid="np-citta" placeholder="Firenze"
                value={form.citta} onChange={(e) => setF("citta", e.target.value)} />
            </div>
            <div>
              <Label>Zona *</Label>
              <Select value={form.zonaId} onValueChange={(v) => setF("zonaId", v)}>
                <SelectTrigger data-testid="np-zona"><SelectValue placeholder="Seleziona zona" /></SelectTrigger>
                <SelectContent>
                  {zone.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: z.colore }} />
                        {z.nome}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>Telefono</Label>
              <Input value={form.telefonoFisso} onChange={(e) => setF("telefonoFisso", e.target.value)} />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={form.telefonoMobile} onChange={(e) => setF("telefonoMobile", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>P. IVA</Label>
              <Input value={form.partitaIva} onChange={(e) => setF("partitaIva", e.target.value)} />
            </div>
            <div>
              <Label>Orario ritiro</Label>
              <Input value={form.orarioRitiro} placeholder="es. 9,00-12,00" onChange={(e) => setF("orarioRitiro", e.target.value)} />
            </div>
            <div>
              <Label>Orario visita</Label>
              <Select value={form.fasciaOrariaVisita} onValueChange={(v) => setF("fasciaOrariaVisita", v)}>
                <SelectTrigger data-testid="np-fascia-visita"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FASCE_VISITA.map((f) => (
                    <SelectItem key={f.v} value={f.v} data-testid={`fascia-visita-opt-${f.v}`}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-3 mt-3">
            <div className="text-sm font-semibold text-slate-700 mb-2">Pianificazione</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label>Frequenza *</Label>
                <Select value={freq} onValueChange={onChangeFreq}>
                  <SelectTrigger data-testid="np-frequenza"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENZE.map((f) => <SelectItem key={f} value={f}>{FREQUENZE_LABEL[f]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {showGiorno && (
                <div>
                  <Label>Giorno della settimana</Label>
                  <Select value={form.pianificazione.giorno} onValueChange={(v) => setP("giorno", v)}>
                    <SelectTrigger data-testid="np-giorno"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GIORNI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showAlternanza && (
                <div className="sm:col-span-2">
                  <Label>Alternanza</Label>
                  <Select value={form.contratto.alternanza || "DISPARI"} onValueChange={(v) => setC("alternanza", v)}>
                    <SelectTrigger data-testid="np-alternanza"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ALTERNANZE.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showSettimanaMese && (
                <div className="sm:col-span-2">
                  <Label>Settimana del mese</Label>
                  <Select value={form.contratto.settimanaMese || "W1"} onValueChange={(v) => setC("settimanaMese", v)}>
                    <SelectTrigger data-testid="np-settimana-mese"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SETTIMANE_MESE.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showMeseAlternanza && (
                <div className="sm:col-span-2">
                  <Label>Mese alternanza</Label>
                  <Select value={form.contratto.meseAlternanza || "PARI"} onValueChange={(v) => setC("meseAlternanza", v)}>
                    <SelectTrigger data-testid="np-mese-alternanza"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MESI_ALTERNANZA.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={onSubmit} disabled={submitting}
            className="bg-[#1B5E20] hover:bg-[#144818] text-white"
            data-testid="np-submit">
            {submitting ? "Salvataggio..." : "Salva produttore"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <GeocodingFallbackDialog
      open={!!fallbackProd}
      citta={fallbackProd?.citta || form.citta}
      onCancel={() => { setFallbackProd(null); onCreated(); }}
      onConfirm={onConfirmFallback}
    />
    </>
  );
}
